import { memo, useState, useCallback, useEffect, useRef } from "react";
import { useI18n } from "@/i18n";
import { Pagination } from "@/design-system";
import { formatDate } from "@/utils/formatDate";
import { listInventoryMovements } from "@/infrastructure/inventory-movement/inventoryMovementService";
import { listProducts } from "@/infrastructure/product/productService";
import { listWarehouses } from "@/infrastructure/warehouse/warehouseService";
import { listMovementSubtypes } from "@/infrastructure/movement/movementService";
import { listTypeUnits } from "@/infrastructure/type-unit/typeUnitService";
import { listCategories } from "@/infrastructure/category/categoryService";
import type { InventoryMovement, ProductMeta, Product, WarehouseRecord, MovementSubtype, TypeUnit, Category } from "@/domain/types";
import { colors, typography, transitions } from "@/design-system/tokens";

const PAGE_SIZE = 8;
const STORAGE_KEY = "nexo-kardex-columns";

type ColumnKey = "date" | "product" | "type" | "quantity" | "unit" | "factor" | "quantityBase" | "balance" | "source" | "category" | "warehouse" | "location" | "reference";

const FIXED_COLUMNS: ColumnKey[] = ["date", "product"];

const DEFAULT_VISIBLE: ColumnKey[] = ["date", "product", "type", "quantity", "balance", "source", "category", "warehouse"];

function loadColumns(): ColumnKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as ColumnKey[];
  } catch {}
  return DEFAULT_VISIBLE;
}

function saveColumns(cols: ColumnKey[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cols)); } catch {}
}

export const KardexPage = memo(function KardexPage() {
  const { t } = useI18n();
  const [items, setItems] = useState<InventoryMovement[]>([]);
  const [meta, setMeta] = useState<ProductMeta>({ totalItems: 0, itemsPerPage: PAGE_SIZE, totalPages: 0, currentPage: 1 });
  const [products, setProducts] = useState<Product[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [subtypes, setSubtypes] = useState<MovementSubtype[]>([]);
  const [units, setUnits] = useState<TypeUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(loadColumns);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [movData, prodData, whData, stData, unitData] = await Promise.all([
        listInventoryMovements({ page, limit: PAGE_SIZE, warehouseId: warehouseFilter || undefined, from: fromDate || undefined, to: toDate || undefined, categoryId: categoryFilter || undefined }),
        products.length === 0 ? listProducts({ limit: 200 }).then(r => r.items) : Promise.resolve(products),
        warehouses.length === 0 ? listWarehouses() : Promise.resolve(warehouses),
        subtypes.length === 0 ? listMovementSubtypes() : Promise.resolve(subtypes),
        units.length === 0 ? listTypeUnits() : Promise.resolve(units),
      ]);
      setItems(movData.items);
      setMeta(movData.meta);
      if (products.length === 0) setProducts(prodData as Product[]);
      if (warehouses.length === 0) setWarehouses(whData as WarehouseRecord[]);
      if (subtypes.length === 0) setSubtypes(stData as MovementSubtype[]);
      if (units.length === 0) setUnits(unitData as TypeUnit[]);
    } catch {}
    setLoading(false);
  }, [page, warehouseFilter, fromDate, toDate, categoryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { listCategories({ limit: 100, status: "ACTIVE" }).then(res => setCategories(res.items)).catch(() => {}); }, []);

  // Close picker on click outside
  useEffect(() => {
    if (!showColumnPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setShowColumnPicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showColumnPicker]);

  const toggleColumn = useCallback((key: ColumnKey) => {
    if (FIXED_COLUMNS.includes(key)) return;
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      saveColumns(next);
      return next;
    });
  }, []);

  const getProductName = useCallback((id: string, code?: string, name?: string) => {
    if (code) return code;
    const p = products.find(pr => pr.id === id);
    return p ? p.code : id.slice(0, 8);
  }, [products]);
  const getUnitAbbr = useCallback((id: string) => units.find(u => u.id === id)?.abbreviation ?? "—", [units]);
  const getWarehouseName = useCallback((id: string, resolved?: string) => {
    if (resolved) return resolved;
    return warehouses.find(w => w.warehouse_id === id)?.warehouse_name ?? "—";
  }, [warehouses]);

  const getSourceLabel = (sourceType: string) => {
    if (sourceType === "receipt") return t.kardex.sourceReceipt;
    if (sourceType === "dispatch") return t.kardex.sourceDispatch;
    if (sourceType === "adjustment") return t.kardex.sourceAdjustment;
    if (sourceType === "transfer") return t.kardex.sourceTransfer;
    return sourceType;
  };

  const allColumns: { key: ColumnKey; label: string }[] = [
    { key: "date", label: t.kardex.colDate },
    { key: "product", label: t.kardex.colProduct },
    { key: "type", label: t.kardex.colType },
    { key: "quantity", label: t.kardex.colQuantity },
    { key: "unit", label: t.kardex.colUnit },
    { key: "factor", label: t.kardex.colFactor },
    { key: "quantityBase", label: t.kardex.colQuantityBase },
    { key: "balance", label: t.kardex.colBalance },
    { key: "source", label: t.kardex.colSource },
    { key: "category", label: t.products.colCategory },
    { key: "warehouse", label: t.kardex.colWarehouse },
    { key: "location", label: t.kardex.colLocation },
    { key: "reference", label: t.kardex.colReference },
  ];

  const activeColumns = allColumns.filter(c => visibleColumns.includes(c.key));

  const renderCell = (item: InventoryMovement, key: ColumnKey) => {
    switch (key) {
      case "date": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{formatDate(item.createdAt)}</span>;
      case "product": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: "#111" }}>{getProductName(item.productId, item.productCode, item.productName)}</span>;
      case "type": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555" }}>{item.movementSubtypeName ?? "—"}</span>;
      case "quantity": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: item.sign > 0 ? "#16a34a" : "#dc2626" }}>{item.sign > 0 ? "+" : "-"}{item.quantity}</span>;
      case "unit": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{item.unitAbbreviation ?? getUnitAbbr(item.typeUnitId)}</span>;
      case "factor": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: item.conversionFactor !== 1 ? "#6366f1" : "#ccc" }}>{item.conversionFactor !== 1 ? `×${item.conversionFactor}` : ""}</span>;
      case "quantityBase": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.semibold, color: item.sign > 0 ? "#16a34a" : "#dc2626" }}>{item.sign > 0 ? "+" : "-"}{item.quantityBase}</span>;
      case "balance": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: "#111" }}>{item.balanceAfter}</span>;
      case "source": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888", background: "rgba(0,0,0,0.04)", padding: "2px 6px", borderRadius: "4px" }}>{getSourceLabel(item.sourceType)}</span>;
      case "category": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{item.categoryName ?? "—"}</span>;
      case "warehouse": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{getWarehouseName(item.warehouseId, item.warehouseName)}</span>;
      case "location": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>{item.locationCode ?? item.locationId?.slice(0, 8) ?? "—"}</span>;
      case "reference": return <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#bbb" }}>{item.referenceDoc ?? "—"}</span>;
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "36px 36px 48px", minHeight: "100vh", background: "#fff", boxSizing: "border-box" }}>
      <div>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: "1.9rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.03em" }}>{t.kardex.title}</h1>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", margin: "4px 0 0" }}>{t.kardex.subtitle}</p>
      </div>

      <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={labelStyle}>{t.kardex.filterWarehouse}</label>
          <select value={warehouseFilter} onChange={e => { setWarehouseFilter(e.target.value); setPage(1); }} style={selectStyle}>
            <option value="">—</option>
            {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={labelStyle}>{t.kardex.filterFrom}</label>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1); }} style={selectStyle} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={labelStyle}>{t.kardex.filterTo}</label>
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1); }} style={selectStyle} />
        </div>
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: categoryFilter ? "#111" : "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", cursor: "pointer" }}>
          <option value="">{t.products.filterAllCategories}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {/* Column picker toggle */}
        <div style={{ position: "relative", marginLeft: "auto" }} ref={pickerRef}>
          <button
            onClick={() => setShowColumnPicker(v => !v)}
            style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#666", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", transition: `all ${transitions.fast}` }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#111"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "#e8e8e8"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 3v18M3 12h18" /><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
            {t.kardex.columns ?? "Columnas"}
          </button>
          {showColumnPicker && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid #e8e8e8", borderRadius: "12px", padding: "12px 16px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, minWidth: "200px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {allColumns.map(col => {
                const isFixed = FIXED_COLUMNS.includes(col.key);
                const isChecked = visibleColumns.includes(col.key);
                return (
                  <label key={col.key} style={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: isFixed ? "#bbb" : "#333", cursor: isFixed ? "default" : "pointer", padding: "4px 0" }}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isFixed}
                      onChange={() => toggleColumn(col.key)}
                      style={{ accentColor: "#111", width: "15px", height: "15px", cursor: isFixed ? "default" : "pointer" }}
                    />
                    {col.label}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: "20px", border: "1px solid #e8e8e8", overflow: "auto", minHeight: "300px" }}>
        {loading && <LoadingState />}
        {!loading && items.length === 0 && <EmptyState />}
        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f2f2f2" }}>
                  {activeColumns.map(col => (
                    <th key={col.key} style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.1em", padding: "14px 12px", textAlign: "left", whiteSpace: "nowrap" }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: "1px solid #f7f7f7", transition: `background ${transitions.fast}` }} onMouseEnter={e => ((e.currentTarget as HTMLTableRowElement).style.background = "#fafafa")} onMouseLeave={e => ((e.currentTarget as HTMLTableRowElement).style.background = "transparent")}>
                    {activeColumns.map(col => (
                      <td key={col.key} style={cell}>{renderCell(item, col.key)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {meta.totalPages > 1 && (
              <div style={{ padding: "0 16px 14px" }}>
                <Pagination currentPage={meta.currentPage} totalPages={meta.totalPages} totalItems={meta.totalItems} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

function EmptyState() {
  const { t } = useI18n();
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px" }}><span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#bbb" }}>{t.kardex.emptyTitle}</span></div>;
}

function LoadingState() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "64px 24px" }}><span style={{ width: "24px", height: "24px", border: `3px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /></div>;
}

const labelStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.xs, fontWeight: typography.weight.semibold, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" };
const selectStyle: React.CSSProperties = { fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#111", background: "#fff", border: "1px solid #e8e8e8", borderRadius: "8px", padding: "9px 14px", outline: "none", cursor: "pointer", minWidth: "100px" };
const cell: React.CSSProperties = { padding: "12px", verticalAlign: "middle" };
